import { useFilePicker } from 'use-file-picker';
import { Button } from '@SBGSports/referee-react';
import { ReactNode, useEffect, useRef } from 'react';
import { useTranslations } from 'i18n';
import { FileValidation } from './types';

interface JsonFileImporterProps<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    json_mapper?: (this: any, key: string, value: any) => any;
    fileValidator: (data: object) => FileValidation;
    setData: (parsedData: T | undefined, fileName: string) => void;
}
export const JsonFileImporter = <T extends object>(props: JsonFileImporterProps<T> & { children?: ReactNode }) => {
    const { json_mapper, fileValidator, setData } = props;
    // Hooks
    const { __ } = useTranslations();
    const { openFilePicker, filesContent, loading, clear } = useFilePicker({
        readAs: 'Text',
        accept: '.json',
        multiple: false,
    });

    /**
     *  Parse data and execute set object callback
     *  Set import result
     */
    const hasSelectedFile = useRef(false);
    useEffect(() => {
        if (filesContent && filesContent.length > 0 && !loading && !hasSelectedFile.current) {
            const fileName = filesContent[0]?.name ?? '';
            try {
                const parsedData = json_mapper
                    ? JSON.parse(filesContent[0].content, json_mapper)
                    : JSON.parse(filesContent[0].content);
                const validationResult = fileValidator(parsedData);
                if (validationResult.isValid) {
                    setData(parsedData, fileName);
                    hasSelectedFile.current = true;
                } else {
                    setData(undefined, fileName);
                }
            } catch (ex) {
                setData(undefined, fileName);
            }
        }
    }, [fileValidator, filesContent, json_mapper, loading, setData]);

    return (
        <div data-testid="json-file-importer">
            <Button
                data-testid="browse-json-file-button"
                onClick={() => {
                    hasSelectedFile.current = false;
                    clear();
                    openFilePicker();
                }}
                disabled={loading}
                variant="outline"
                loading={loading}
            >
                {__('smt_importer.browse')}
            </Button>
        </div>
    );
};
